/**
 * Server-side 2nd-order Euler REFINE client (Phase C, FXC-9009).
 *
 * The browser runs the 1st-order f32 WebGPU Euler solve LIVE (see `backend.ts`).
 * This client offers an OPTIONAL, higher-fidelity refinement: it POSTs the
 * just-solved mesh to a local dev backend (`tools/euler_refine_service.py` in
 * the FlexFoil_RANS repo) that runs the CPU-f64 reference solver
 * (`flexfoil._rustfoil.analyze_euler_mesh`) with `second_order=true` — a
 * warm-started JFNK driving the 2nd-order MUSCL residual to ~0.
 *
 * A 2nd-order solve is ~10 MINUTES on a large multi-element mesh, so this is a
 * FAITHFUL REFINE, not an interactive solve. The service uses a job pattern:
 * POST returns a `job_id` immediately; we poll GET until it is done.
 *
 * Gating: the workbench only surfaces this when `VITE_EULER_REFINE_URL` is set
 * (see `HighliftMultifidelitySurface.tsx`), so the default build is untouched.
 *
 * The mesh sent is the SAME `FlowMesh` (`points` + `tris`) the WebGPU solve
 * produced, serialized to the legacy ASCII VTK the importer reads (it parses
 * `POINTS n double` then `CELLS ncells nints` rows `3 i j k`, ignoring
 * `CELL_TYPES`). z is written as 0 (the mesh is planar).
 */

/** Default dev endpoint — overridden by `VITE_EULER_REFINE_URL`. */
export const DEFAULT_EULER_REFINE_URL = "http://127.0.0.1:8723";

/** Read the configured refine endpoint (or the loopback default). */
export function refineBaseUrl(): string {
  const url = import.meta.env.VITE_EULER_REFINE_URL;
  return (typeof url === "string" && url.trim()) || DEFAULT_EULER_REFINE_URL;
}

/** True when the refine backend is configured — gate the affordance on this. */
export function refineEnabled(): boolean {
  return typeof import.meta.env.VITE_EULER_REFINE_URL === "string"
    && import.meta.env.VITE_EULER_REFINE_URL.trim().length > 0;
}

/** The coefficients the refine returns, normalised to the workbench's camelCase. */
export interface RefineResult {
  cl: number;
  clMain?: number;
  clFlap?: number;
  cd: number;
  cdPressure?: number;
  converged?: boolean;
  residualOrders?: number;
  newtonIters?: number;
}

/** One operating point to refine. `meshVtk` is legacy-ASCII VTK (see `flowMeshToVtk`). */
export interface RefineRequest {
  meshVtk: string;
  alphaDeg: number;
  mach: number;
  /** Default true — the whole point is the 2nd-order refine. */
  secondOrder?: boolean;
}

/** The solver result dict the service surfaces (snake_case, mirrors analyze_euler_mesh). */
interface RawRefineResult {
  cl: number;
  cl_main?: number;
  cl_flap?: number;
  cd: number;
  cd_pressure?: number;
  converged?: boolean;
  residual_orders?: number;
  newton_iters?: number;
  success?: boolean;
  error?: string | null;
}

/** GET /refine/{job_id} response. */
interface PollResponse {
  status: "running" | "done" | "error";
  result?: RawRefineResult;
  error?: string;
}

/** A minimal flat-array triangle mesh — the subset of `FlowMesh` the refine needs. */
export interface RefineMesh {
  /** Flat `[x0,y0,x1,y1,…]` node coordinates (UI/chord frame; z := 0). */
  points: number[];
  /** Flat `[i0,j0,k0,…]` triangle node indices into `points`. */
  tris: number[];
}

/**
 * Serialize a flat-array triangle mesh to the legacy ASCII VTK the Rust importer
 * (`from_legacy_vtk_str`) reads: a `POINTS n double` block (x y z per node, z=0)
 * and a `CELLS ncells nints` block of `3 i j k` triangle rows. `CELL_TYPES` is
 * emitted for completeness but the importer ignores it. Throws on a malformed
 * mesh (odd `points`, non-triple `tris`) so a bad serialize fails loud, not on
 * the server.
 */
export function flowMeshToVtk(mesh: RefineMesh): string {
  const { points, tris } = mesh;
  if (points.length % 2 !== 0) {
    throw new Error(`flowMeshToVtk: points length ${points.length} is not even`);
  }
  if (tris.length % 3 !== 0) {
    throw new Error(`flowMeshToVtk: tris length ${tris.length} is not a multiple of 3`);
  }
  const nPoints = points.length / 2;
  const nTris = tris.length / 3;

  const lines: string[] = [
    "# vtk DataFile Version 3.0",
    "thread webgpu refine mesh",
    "ASCII",
    "DATASET UNSTRUCTURED_GRID",
    `POINTS ${nPoints} double`,
  ];
  for (let i = 0; i < nPoints; i++) {
    const x = points[i * 2]!;
    const y = points[i * 2 + 1]!;
    lines.push(`${x} ${y} 0`);
  }
  // CELLS header: ncells, then the total integer count (each tri row is 4 ints: "3 i j k").
  lines.push(`CELLS ${nTris} ${nTris * 4}`);
  for (let i = 0; i < nTris; i++) {
    lines.push(`3 ${tris[i * 3]!} ${tris[i * 3 + 1]!} ${tris[i * 3 + 2]!}`);
  }
  lines.push(`CELL_TYPES ${nTris}`);
  for (let i = 0; i < nTris; i++) lines.push("5"); // VTK_TRIANGLE
  return lines.join("\n") + "\n";
}

/** Normalise the service's snake_case dict into the workbench's camelCase result. */
function liftRefine(raw: RawRefineResult): RefineResult {
  if (raw.success === false) throw new Error(raw.error ?? "refine solve failed");
  return {
    cl: raw.cl,
    clMain: raw.cl_main,
    clFlap: raw.cl_flap,
    cd: raw.cd,
    cdPressure: raw.cd_pressure,
    converged: raw.converged,
    residualOrders: raw.residual_orders,
    newtonIters: raw.newton_iters,
  };
}

/** Progress callback surface for the (long) poll — mirrors the WebGPU onProgress shape. */
export interface RefineProgress {
  status: "submitting" | "running" | "done" | "error";
  /** Wall-clock seconds since submit (the only honest progress for an opaque JFNK). */
  elapsedSec: number;
}

export interface RefineOptions {
  /** Override the base URL (defaults to `refineBaseUrl()`). */
  baseUrl?: string;
  /** Poll cadence in ms (default 5000 — the solve is ~10 min). */
  pollIntervalMs?: number;
  /** Abort the poll (e.g. on unmount / backend switch). */
  signal?: AbortSignal;
  /** Progress ticks (submit → each poll → done). */
  onProgress?: (p: RefineProgress) => void;
}

async function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const id = setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(id);
        reject(new DOMException("refine aborted", "AbortError"));
      },
      { once: true },
    );
  });
}

/**
 * Submit a 2nd-order refine and poll to completion. POSTs `/refine` for a job id,
 * then GETs `/refine/{id}` every `pollIntervalMs` until the status is `done`
 * (resolves the lifted result) or `error` (throws). Honours `signal` so the
 * caller can cancel a long poll on unmount or a backend switch.
 */
export async function refineSecondOrder(
  req: RefineRequest,
  opts: RefineOptions = {},
): Promise<RefineResult> {
  const base = (opts.baseUrl ?? refineBaseUrl()).replace(/\/$/, "");
  const pollIntervalMs = opts.pollIntervalMs ?? 5000;
  const startedAt = Date.now();
  const elapsed = () => (Date.now() - startedAt) / 1000;

  opts.onProgress?.({ status: "submitting", elapsedSec: 0 });
  const postRes = await fetch(`${base}/refine`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      mesh_vtk: req.meshVtk,
      alpha_deg: req.alphaDeg,
      mach: req.mach,
      second_order: req.secondOrder ?? true,
    }),
    signal: opts.signal,
  });
  if (!postRes.ok) {
    throw new Error(`refine POST failed: ${postRes.status} ${postRes.statusText}`);
  }
  const { job_id: jobId } = (await postRes.json()) as { job_id: string };
  if (!jobId) throw new Error("refine POST returned no job_id");

  // Poll until done. The solve is ~10 min; the loop is bounded only by `signal`.
  for (;;) {
    await sleep(pollIntervalMs, opts.signal);
    const pollRes = await fetch(`${base}/refine/${jobId}`, { signal: opts.signal });
    if (!pollRes.ok) {
      throw new Error(`refine poll failed: ${pollRes.status} ${pollRes.statusText}`);
    }
    const snap = (await pollRes.json()) as PollResponse;
    if (snap.status === "running") {
      opts.onProgress?.({ status: "running", elapsedSec: elapsed() });
      continue;
    }
    if (snap.status === "error") {
      opts.onProgress?.({ status: "error", elapsedSec: elapsed() });
      throw new Error(snap.error ?? "refine job errored");
    }
    // done
    opts.onProgress?.({ status: "done", elapsedSec: elapsed() });
    if (!snap.result) throw new Error("refine job done with no result");
    return liftRefine(snap.result);
  }
}
